import { DatePicker, Grid, GridColumn, GridRow, Select, SelectOptionType } from '@bp/ui-components';
import dayjs from 'dayjs';
import { Formik } from 'formik';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Form } from 'react-router-dom';
import { MultiValue } from 'react-select';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { useSelectedSchoolYear } from '../../../hooks/useSelectedSchoolYear';
import { blockIfEventJobRunning, createEventsForDateRange } from '../../../utils/eventsApi';
import { showSuccessCreateToast, showSuccessUpdateToast, showUserDbErrorToast } from '../../../utils/toast';
import { ModalBottomButtons } from '../../ModalBottomButtons/ModalBottomButtons';
import { useTimetables } from '../../Timetable/hooks/useTimetables';
import { TimetableBlockFormValues } from '../graphql';
import { ProcessedTimetableBlockType, useTimetableBlock } from '../useTimetableBlock';
import styles from './TimetableBlockForm.module.scss';
import { findTimetableClassConflicts } from './timetableClassConflicts';
import { timetableBlockSchema } from './validation/schema';

type TimetableBlockFormProps = {
  block: ProcessedTimetableBlockType;
  canEditStart: boolean;
  canEditEnd: boolean;
  canEditTimetables: boolean;
  onClose: () => void;
};

export const TimetableBlockForm = ({
  block,
  canEditStart,
  canEditEnd,
  canEditTimetables,
  onClose,
}: TimetableBlockFormProps) => {
  const { t } = useTranslation();
  const schoolYear = useSelectedSchoolYear();
  const { pimAuthClaims } = useAuthClaims();

  const { createTimetableBlock, updateTimetableBlock, getFormData, getEarliestEnd, getLatestStart } =
    useTimetableBlock();

  const { timetables: loadedTimetables } = useTimetables(schoolYear?.uuid);
  const timetables = useMemo(() => loadedTimetables ?? [], [loadedTimetables]);

  const initialValues: TimetableBlockFormValues = getFormData(block.uuid);

  const initialTimetableOptions = useMemo(() => {
    // Nur Stundenpläne mit aktiver Version dürfen einem Abschnitt hinzugefügt werden.
    // Bereits zugeordnete Stundenpläne bleiben erhalten, damit sie im Edit-Modus nicht
    // still aus der Auswahl fallen.
    const assignedUuids = new Set((initialValues.timetables ?? []).map((tt) => tt.uuid));
    return timetables
      .filter((tt) => tt.versions.some((v) => v.active) || assignedUuids.has(tt.uuid))
      .map((tt) => ({
        label: tt.name,
        value: tt.uuid,
      }));
  }, [timetables, initialValues.timetables]);

  // Stundenpläne, die eine Klasse mit einem gewählten teilen, sind nicht wählbar (ein Abschnitt, eine Version je Klasse)
  const timetableOptionsFor = (values: TimetableBlockFormValues) => {
    const selectedUuids = values.timetables.map((timetable) => timetable.uuid);
    const { blocked } = findTimetableClassConflicts(selectedUuids, timetables);
    return initialTimetableOptions.filter((option) => !blocked.has(option.value));
  };

  const findConflicts = useCallback(
    (timetableUuids: string[]) =>
      findTimetableClassConflicts(timetableUuids, timetables).conflicting.map((timetable) => timetable.name),
    [timetables],
  );

  async function handleSubmit(values: TimetableBlockFormValues) {
    if (await blockIfEventJobRunning()) return;

    let blockUuid: string | undefined;

    if (block.empty) {
      const result = await createTimetableBlock(values);
      if (result.error) {
        showUserDbErrorToast();
        onClose();
        return;
      }
      blockUuid = result.data?.createTimetableBlock.uuid;
      showSuccessCreateToast();
    } else {
      const result = await updateTimetableBlock(values, block.uuid);
      if (result.error) {
        showUserDbErrorToast();
        onClose();
        return;
      }
      blockUuid = block.uuid;
      showSuccessUpdateToast();
    }
    const blockEndsInFutureOrNow = dayjs(values.end).isSameOrAfter(dayjs(), 'day');
    if (blockEndsInFutureOrNow && blockUuid && schoolYear) {
      const from = dayjs(values.start).isBefore(dayjs(initialValues.start))
        ? dayjs(values.start).format('YYYY-MM-DD')
        : dayjs(initialValues.start).format('YYYY-MM-DD');
      const to = dayjs(values.end).isAfter(dayjs(initialValues.end))
        ? dayjs(values.end).format('YYYY-MM-DD')
        : dayjs(initialValues.end).format('YYYY-MM-DD');
      await createEventsForDateRange({
        organizationUuid: pimAuthClaims.getOrganizationUuid(),
        schoolYearUuid: schoolYear.uuid,
        from,
        to,
      });
    }

    onClose();
  }

  const notBefore = getLatestStart(initialValues.uuid);
  const notAfter = getEarliestEnd(initialValues.uuid);

  // Zu einem Nachbarabschnitt muss ein Tag Abstand bleiben; die Schuljahresgrenzen selbst sind
  // dagegen wählbar – der erste Abschnitt darf am Schuljahresbeginn starten, der letzte am
  // Schuljahresende enden. Zusätzlich wird auf das Schuljahr geklemmt, falls ein Abschnitt aus
  // inkonsistenten Daten (z.B. nachträglich verkürztes Schuljahr) darüber hinausragt.
  let min = notBefore.isSchoolYearBoundary ? dayjs(notBefore.date) : dayjs(notBefore.date).add(1, 'day');
  let max = notAfter.isSchoolYearBoundary ? dayjs(notAfter.date) : dayjs(notAfter.date).subtract(1, 'day');

  if (schoolYear) {
    const schoolYearStart = dayjs(schoolYear.start);
    const schoolYearEnd = dayjs(schoolYear.end);
    if (min.isBefore(schoolYearStart, 'day')) min = schoolYearStart;
    if (max.isAfter(schoolYearEnd, 'day')) max = schoolYearEnd;
  }

  // Tagesränder statt Tages-Mitternacht: die Formularwerte tragen je nach Herkunft Uhrzeiten
  // (`new Date('2027-07-31')` ist UTC-Mitternacht, lokal also 02:00) – mit `startOf('day')` als
  // Obergrenze fiele der letzte zulässige Tag sonst aus dem Bereich.
  const minDate = min.startOf('day');
  const maxDate = max.endOf('day');
  const minTime = minDate.valueOf();
  const maxTime = maxDate.valueOf();

  const validationSchema = useMemo(
    () =>
      timetableBlockSchema({
        minDate: new Date(minTime),
        maxDate: new Date(maxTime),
        // Läuft der Abschnitt schon, sind die Stundenpläne gesperrt: Altdaten mit gemeinsamen Klassen dürfen dann
        // das Ändern der Daten nicht blockieren (Hinweis steht am Abschnitt).
        findConflicts: canEditTimetables ? findConflicts : undefined,
      }),
    [minTime, maxTime, findConflicts, canEditTimetables],
  );

  return (
    <div className={styles['timetable-block-form']}>
      <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validationSchema}>
        {({
          values,
          setFieldValue,
          setFieldTouched,
          resetForm,
          isSubmitting,
          isValidating,
          isValid,
          dirty,
          errors,
          touched,
        }) => {
          return (
            <Form>
              <Grid useFormGap>
                <GridRow spacingBottom='s'>
                  <GridColumn width={6}>
                    <DatePicker
                      minDate={minDate.toDate()}
                      maxDate={maxDate.toDate()}
                      label={t('common.start')}
                      value={values.start}
                      onChange={async (date) => {
                        await setFieldTouched('start');
                        await setFieldValue('start', date, true);
                      }}
                      name='start'
                      disabled={!values.timetables || values.timetables.length === 0 || !canEditStart}
                      error={errors.start as string}
                    />
                  </GridColumn>
                  <GridColumn width={6}>
                    <DatePicker
                      label={t('common.end')}
                      value={values.end}
                      minDate={minDate.toDate()}
                      maxDate={maxDate.toDate()}
                      onChange={async (date) => {
                        await setFieldTouched('end');
                        await setFieldValue('end', date, true);
                      }}
                      name='end'
                      disabled={!values.timetables || values.timetables.length === 0 || !canEditEnd}
                      error={errors.end as string}
                    />
                  </GridColumn>
                </GridRow>
                <GridRow spacingTop='s' spacingBottom='s'>
                  <Select
                    name='timetables'
                    isSearchable
                    isClearable
                    disabled={!canEditTimetables}
                    isMulti
                    menuPosition={'fixed'}
                    label={t('timetable.active')}
                    // kontrolliert: das X am Eintrag entfernt anhand von `value` (mit `defaultValue` lief es ins Leere)
                    value={values.timetables.map((timetable) => {
                      return { value: timetable.uuid, label: timetable.name };
                    })}
                    options={timetableOptionsFor(values)}
                    onChange={async (option) => {
                      const options = option as MultiValue<SelectOptionType>;
                      const timetable = timetables.find((timetable) =>
                        options.find((option) => timetable.uuid === option.value),
                      );
                      if (!option || !options || options.length === 0 || !timetable) {
                        resetForm();
                        setFieldValue('timetables', []);
                      } else {
                        if (!touched.start) {
                          await setFieldValue('start', values.start);
                          await setFieldTouched('start');
                        }
                        if (!touched.end) {
                          await setFieldValue('end', values.end);
                          await setFieldTouched('end');
                        }
                        await setFieldValue(
                          'timetables',
                          options.map((option) => ({ uuid: option.value as string, name: option.label })),
                        );
                      }
                    }}
                    error={errors.timetables as string}
                  />
                </GridRow>
              </Grid>
              <ModalBottomButtons
                closeButton={{
                  callback: () => {
                    resetForm();
                    onClose();
                  },
                  text: t('common.cancelChanges'),
                }}
                submitButton={{
                  // Der Button ruft handleSubmit direkt auf, umgeht also Formiks Submit-Validierung –
                  // ohne isValid liessen sich Werte ausserhalb des Schuljahres speichern.
                  disabled: isSubmitting || !dirty || isValidating || !isValid,
                  callback: () => handleSubmit(values),
                }}
                isLoading={isSubmitting}
                errors={errors}
              />
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};
